import { create } from "zustand";
import {
  getTodayAttendance,
  punchIn,
  punchOut,
  updateDailyRemarks,
  getAttendanceHistory,
  getAttendanceSummary,
  getTeamAttendance,
  getUserDailyUpdateFlag
} from "@/services/EmployeeService/AttendanceService";
import { registerStoreReset } from "@/store/storeResetUtils";

interface AttendanceRecord {
  id: number;
  employee_id: string;
  attendance_date: string;
  punch_in: string;
  punch_out: string | null;
  total_hours: number | null;
  status: 'present' | 'absent' | 'half-day';
  remarks: string | null;
}

interface AttendanceSummary {
  total_days: number;
  present_days: number;
  absent_days: number;
  half_days: number;
  avg_hours: number;
  total_hours_worked: number;
  late_count: number;
  incomplete_days: number;
}

interface TeamMember {
  employee_id: string;
  employee_name: string;
  employee_email: string;
  employee_contact: string | null;
  attendance_date: string | null;
  punch_in: string | null;
  punch_out: string | null;
  total_hours: number | null;
  status: 'present' | 'absent' | 'half-day' | null;
  remarks: string | null;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface AttendanceStore {
  // State
  todayAttendance: AttendanceRecord | null;
  attendanceHistory: AttendanceRecord[];
  attendanceSummary: AttendanceSummary | null;
  teamAttendance: TeamMember[];
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  dailyUpdateFlag: number | null;
  isDailyUpdateMandatory: boolean;
  dailyUpdateMessage: string;

  // Actions
  fetchTodayAttendance: () => Promise<void>;
  handlePunchIn: () => Promise<boolean>;
  handlePunchOut: (flagDailyUpdate?: number) => Promise<boolean>;
  handleUpdateRemarks: (remarks: string) => Promise<boolean>;
  fetchAttendanceHistory: (page?: number, limit?: number) => Promise<void>;
  fetchAttendanceSummary: (startDate?: string, endDate?: string) => Promise<void>;
  fetchTeamAttendance: (date: string) => Promise<void>;
  fetchDailyUpdateFlag: () => Promise<void>;
  resetMessages: () => void;
  resetError: () => void;
  resetStore: () => void;
}

const useAttendanceStore = create<AttendanceStore>((set, get) => ({
  // Initial state
  todayAttendance: null,
  attendanceHistory: [],
  attendanceSummary: null,
  teamAttendance: [],
  pagination: null,
  loading: false,
  error: null,
  successMessage: null,
  dailyUpdateFlag: null,
  isDailyUpdateMandatory: false,
  dailyUpdateMessage: '',

  // Fetch today's attendance
  fetchTodayAttendance: async () => {
    try {
      set({ loading: true, error: null });

      const response = await getTodayAttendance();
      console.log("Today Attendance API Response:", response);

      if (response.success) {
        set({ todayAttendance: response.data, loading: false });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch today\'s attendance',
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Today Attendance Error:", error);
      set({ error: error.message, loading: false });
    }
  },

  // Handle punch in
  handlePunchIn: async () => {
    try {
      set({ loading: true, error: null, successMessage: null });

      const response = await punchIn();
      console.log("Punch In API Response:", response);

      if (response.success) {
        set({
          todayAttendance: response.data,
          loading: false,
          successMessage: response.message || 'Punched in successfully'
        });
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to punch in',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Punch In Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Handle punch out
  handlePunchOut: async (flagDailyUpdate?: number) => {
    try {
      set({ loading: true, error: null, successMessage: null });

      // Check if daily update is mandatory (flag_daily_update = 0)
      const todayAttendance = get().todayAttendance;
      if (flagDailyUpdate === 0 && (!todayAttendance?.remarks || todayAttendance.remarks.trim() === '')) {
        set({
          error: 'Please submit your daily update before punching out',
          loading: false
        });
        return false;
      }

      const response = await punchOut();
      console.log("Punch Out API Response:", response);

      if (response.success) {
        set({
          todayAttendance: response.data,
          loading: false,
          successMessage: response.message || 'Punched out successfully'
        });
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to punch out',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Punch Out Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Handle update remarks
  handleUpdateRemarks: async (remarks: string) => {
    try {
      set({ loading: true, error: null, successMessage: null });

      const response = await updateDailyRemarks(remarks);
      console.log("Update Remarks API Response:", response);

      if (response.success) {
        set({
          todayAttendance: response.data,
          loading: false,
          successMessage: response.message || 'Daily update added successfully'
        });
        return true;
      } else {
        set({
          error: response.error?.message || 'Failed to update daily remarks',
          loading: false
        });
        return false;
      }
    } catch (error: any) {
      console.error("Update Remarks Error:", error);
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Fetch attendance history
  fetchAttendanceHistory: async (page = 1, limit = 30) => {
    try {
      set({ loading: true, error: null });

      const response = await getAttendanceHistory(page, limit);
      console.log("Attendance History API Response:", response);

      if (response.success) {
        set({
          attendanceHistory: response.data,
          pagination: response.pagination,
          loading: false
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch attendance history',
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Attendance History Error:", error);
      set({ error: error.message, loading: false });
    }
  },

  // Fetch attendance summary
  fetchAttendanceSummary: async (startDate?: string, endDate?: string) => {
    try {
      set({ loading: true, error: null });

      const response = await getAttendanceSummary(startDate || undefined, endDate || undefined);
      console.log("Attendance Summary API Response:", response);

      if (response.success) {
        set({
          attendanceSummary: response.data,
          loading: false
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch attendance summary',
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Attendance Summary Error:", error);
      set({ error: error.message, loading: false });
    }
  },

  // Fetch team attendance
  fetchTeamAttendance: async (date: string) => {
    try {
      set({ loading: true, error: null });

      const response = await getTeamAttendance(date);
      console.log("Team Attendance API Response:", response);

      if (response.success) {
        set({
          teamAttendance: response.data,
          loading: false
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch team attendance',
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Team Attendance Error:", error);
      set({ error: error.message, loading: false });
    }
  },

  // Fetch daily update flag
  fetchDailyUpdateFlag: async () => {
    try {
      const response = await getUserDailyUpdateFlag();
      console.log("Daily Update Flag API Response:", response);

      if (response.success && response.data) {
        set({
          dailyUpdateFlag: response.data.flag_daily_update,
          isDailyUpdateMandatory: response.data.is_mandatory,
          dailyUpdateMessage: response.data.message || ''
        });
      }
    } catch (error: any) {
      console.error("Fetch Daily Update Flag Error:", error);
    }
  },

  // Reset messages
  resetMessages: () => set({ error: null, successMessage: null }),

  // Reset error
  resetError: () => set({ error: null }),

  // Reset store to initial state
  resetStore: () => set({
    todayAttendance: null,
    attendanceHistory: [],
    attendanceSummary: null,
    teamAttendance: [],
    pagination: null,
    loading: false,
    error: null,
    successMessage: null,
    dailyUpdateFlag: null,
    isDailyUpdateMandatory: false,
    dailyUpdateMessage: ''
  })
}));

// Register this store for centralized reset
registerStoreReset('attendance', () => useAttendanceStore.getState().resetStore());

export default useAttendanceStore;
